"use client"

import * as React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faChevronDown,
  faChevronRight,
} from "@instadeepai/fontawesome-pro-regular"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@radix-ui/react-popover"
import { cva, type VariantProps } from "class-variance-authority"
import Image from "next/image"
import Link from "next/link"

import { cn } from "../../lib/utils"

const breadcrumbVariants = cva("px-2 py-1 text-sm leading-5 transition-colors", {
  variants: {
    current: {
      false: "font-medium text-[#475467] hover:text-[#344054] hover:bg-[#F9FAFB] hover:rounded-[6px]",
      true: "text-[#344054] font-semibold rounded-[6px] bg-[#F9FAFB]",
    },
    isReturn: {
      false: "",
      true: "text-primary-700 hover:text-primary-900 font-medium",
    },
  },
  defaultVariants: {
    current: false,
    isReturn: false,
  },
})

interface BreadcrumbListProps extends React.ComponentPropsWithoutRef<"ol"> {
  children?: React.ReactNode
}

interface BreadcrumbItemProps extends React.ComponentPropsWithoutRef<"li"> {
  children?: React.ReactNode
}

interface BreadcrumbLinkProps
  extends React.ComponentPropsWithoutRef<"a">,
  VariantProps<typeof breadcrumbVariants> {
  href?: string
  children?: React.ReactNode
}

interface BreadcrumbPageProps
  extends React.ComponentPropsWithoutRef<"span">,
  VariantProps<typeof breadcrumbVariants> {
  children?: React.ReactNode
}

interface BreadcrumbReturnProps
  extends React.ComponentPropsWithoutRef<"a">,
  VariantProps<typeof breadcrumbVariants> {
  href?: string
  children?: React.ReactNode
}

const BreadcrumbList = React.forwardRef<HTMLOListElement, BreadcrumbListProps>(
  ({ className, children, ...props }, ref) => (
    <ol
      ref={ref}
      className={cn("flex items-center gap-2", className)}
      {...props}
    >
      {children}
    </ol>
  )
)
BreadcrumbList.displayName = "BreadcrumbList"

const BreadcrumbItem = React.forwardRef<HTMLLIElement, BreadcrumbItemProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <li ref={ref} className={className} {...props}>
        {children}
      </li>
    )
  }
)
BreadcrumbItem.displayName = "BreadcrumbItem"

const BreadcrumbLink = React.forwardRef<HTMLAnchorElement, BreadcrumbLinkProps>(
  ({ className, href, children, ...props }, ref) => {
    return (
      <Link
        ref={ref}
        href={href ?? "/"}
        className={cn(breadcrumbVariants({ current: false }), className)}
        {...props}
      >
        {children}
      </Link>
    )
  }
)
BreadcrumbLink.displayName = "BreadcrumbLink"

const BreadcrumbReturn = React.forwardRef<
  HTMLAnchorElement,
  BreadcrumbReturnProps
>(({ className, href, children, ...props }, ref) => {
  return (
    <a
      ref={ref}
      href={href}
      className={cn(breadcrumbVariants({ isReturn: true }), className)}
      {...props}
    >
      {children}
    </a>
  )
})
BreadcrumbReturn.displayName = "BreadcrumbReturn"

const BreadcrumbPage = React.forwardRef<HTMLSpanElement, BreadcrumbPageProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(breadcrumbVariants({ current: true }), className)}
        aria-current="page"
        {...props}
      >
        {children}
      </span>
    )
  }
)
BreadcrumbPage.displayName = "BreadcrumbPage"

const BreadcrumbSeparator = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<"span">) => (
  <span className={cn("text-gray-100", className)} {...props}>
    {children}
  </span>
)
BreadcrumbSeparator.displayName = "BreadcrumbSeparator"

interface BreadcrumbEllipsisProps
  extends React.ComponentPropsWithoutRef<"span"> {
  menuItems: {
    href?: string
    label: string
  }[]
  currentPath?: string
}

const BreadcrumbEllipsis = React.forwardRef<
  HTMLButtonElement,
  BreadcrumbEllipsisProps
>(({ className, menuItems, currentPath, ...props }, ref) => {
  return (
    <Popover>
      <PopoverTrigger
        ref={ref}
        className={cn(
          "flex items-center justify-between gap-1 text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded px-2 py-1 transition-colors",
          className
        )}
        {...props}
      >
        <span>...</span>
      </PopoverTrigger>
      <PopoverContent
        className="bg-white shadow-md p-1 border rounded-md outline-none w-40 animate-in fade-in-0 zoom-in-95"
        align="start"
        side="bottom"
        sideOffset={4}
      >
        <div className="flex flex-col gap-1">
          {menuItems.map((menuItem) => {
            const isActive = currentPath === menuItem.href
            return (
              <div
                key={menuItem.label}
                className={cn(
                  "relative flex items-center px-2 py-1.5 rounded-sm outline-none text-sm transition-colors cursor-pointer",
                  isActive 
                    ? "bg-gray-100 text-gray-900 font-medium" 
                    : "hover:bg-gray-50 focus:bg-gray-50 hover:font-medium focus:font-medium hover:text-gray-950 focus:text-gray-950"
                )}
              >
                {menuItem.href ? (
                  <BreadcrumbLink href={menuItem.href}>
                    {menuItem.label}
                  </BreadcrumbLink>
                ) : (
                  menuItem.label
                )}
              </div>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
})
BreadcrumbEllipsis.displayName = "BreadcrumbEllipsis"

interface BreadcrumbMenuItem {
  label: string
  href?: string
}

interface BreadcrumbItemType {
  type: "link" | "page" | "dropdown" // Type of breadcrumb
  label?: string
  href?: string
  menuItems?: BreadcrumbMenuItem[]
}

interface BreadcrumbProps {
  items: BreadcrumbItemType[]
  className?: string
  homeHref: string
  currentPath?: string
}

const Breadcrumb = ({ items, className, homeHref, currentPath }: BreadcrumbProps) => {
  return (
    <BreadcrumbList className={className}>
      <BreadcrumbItem className="flex items-center py-1">
        <BreadcrumbLink href={homeHref}>
          <Image
            src="/home-line.svg"
            alt="Home"
            width={20}
            height={20}
            className="text-gray-500 hover:text-gray-950"
          />
        </BreadcrumbLink>
        <BreadcrumbSeparator className="flex items-center">
          <FontAwesomeIcon icon={faChevronRight} className="size-2.5 text-gray-300" />
        </BreadcrumbSeparator>
      </BreadcrumbItem>
      {items.map((item) => (
        <React.Fragment key={item.label}>
          <BreadcrumbItem className="flex items-center">
            {item.type === "link" && item.href && (
              <BreadcrumbLink href={item.href}>{item.label}</BreadcrumbLink>
            )}
            {item.type === "page" && (
              <BreadcrumbPage>{item.label}</BreadcrumbPage>
            )}
            {item.type === "dropdown" && item.menuItems && (
              <BreadcrumbEllipsis menuItems={item.menuItems} currentPath={currentPath} />
            )}
            {item.type !== "page" && (
              <BreadcrumbSeparator className="flex items-center">
                <FontAwesomeIcon icon={faChevronRight} className="size-2.5 text-gray-300" />
              </BreadcrumbSeparator>
            )}
          </BreadcrumbItem>
        </React.Fragment>
      ))}
    </BreadcrumbList>
  )
}

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
  type BreadcrumbItemType,
}
